import React from 'react'
import './HomePage.css' // We will add new styles here
import '../../shared/shared.css'
import Search from '../../shared/search/Search'
import { useData } from '../../shared/contexts/DataContext.js'
import { useScrapeDate } from '../../shared/contexts/ScrapeDateContext.js'
import ExportMenu from '../../pages/data/selected_data/export_menu/ExportMenu.js'
import { useNavigate } from 'react-router-dom'
import { useTourRun } from '../../core/tour/DashboardTour'
import { useTableUtils } from '../../shared/contexts/TableUtilsContext'
import { MedicineGuessGame } from '../../shared/guess-game/guess-game.js'
import AnalyticsOverview from './AnalyticsOverview.js'
import QuickOverview from './QuickOverview.js'
import config from '../../config/config'
import { slashDateToStringDate, formatSlashDateToISO } from '../data/shared/table/format.js'

// Function based component rendering the home page
function HomePage() {
  const navigate = useNavigate()
  const runTour = useTourRun()
  const { tableUtils, setTableUtils } = useTableUtils()
  const data = useData()
  const scrapeDate = useScrapeDate();

  // Set the query in the utils context and navigate to the datapage
  const search = (query) => {
    setTableUtils({ ...tableUtils, search: query })
    if (query !== '') {
      navigate(config.routes.main.data);
    }
  }

  return (
    // Homepage components, starting with new hero and action grid
    <div className="med-home-content">
      {/* NEW HERO SECTION 
        Provides a clear starting point with a welcome and prominent search.
      */}
      <Search update={search} />
      <div className="med-content-container">
        <section className="home-hero">
          <h1>Welcome to the EMRD Dashboard</h1>
          <p class="home-subtitle">
            Instantly access and analyse data about medicines regulated in the EU.
          </p>
        </section>

        {/* NEW ACTION CARD SECTION
        Replaces the old button list. This is much clearer and
        integrates the Tour as a primary, "friendly" action.
      */}
        <section className="home-actions-container">
          <button
            className="home-action-card med-primary-solid"
            onClick={() => navigate(config.routes.main.data)}
          >
            <i className="bx bx-data"></i>
            <h3>Browse All Medicines Data</h3>
            <p>Explore, filter, and select from the complete dataset.</p>
          </button>

          <button
            className="home-action-card med-primary-solid"
            onClick={() => (window.location.href = config.routes.main.docShark)}
          >
            <i className="bx bx-search-alt"></i>
            <h3>Search Authorisation Documents (DocShark)</h3>
            <p>Find specific text in regulatory documents (e.g., EPARs, SmPCs, EC Decisions).</p>
          </button>

          <button
            className="home-action-card med-primary-solid"
            onClick={() => navigate('/atc')}
          >
            <i className="bx bx-sitemap"></i>
            <h3>ATC Classification Overview</h3>
            <p>An overview of all medicines based on the Anatomical Therapeutic Chemical (ATC) classification system.</p>
          </button>

          <button
            className="home-action-card med-primary-solid"
            onClick={() => navigate('/substance_type')}
          >
            <i className="bx bx-vial"></i>
            <h3>Substance Type Overview</h3>
            <p>An overview of all medicines per substance type.</p>
          </button>

          <button
            className="home-action-card med-primary-solid" // Styled differently to be inviting
            onClick={() => runTour(true)}
          >
            <i className="bx bx-walk"></i>
            <h3>Take the Guided Tour</h3>
            <p>New here? Let us show you all the features.</p>
          </button>

          {/* The ExportMenu is now also a "card" to fit the design */}
          <ExportMenu
            selectedData={data} // Assuming 'data' is available in this scope
            text="Download All Data" // The 'text' prop is now optional here, but kept for clarity
          // No className needed here, the class is on the children
          >
            {/* Your Custom Button Structure goes here as children */}
            <div className="home-action-card med-primary-solid">
              <i className="bx bx-export"></i>
              <h3>Export All Data</h3>
              <p>Download the complete dataset in various formats (e.g., Excel, CSV)</p>
            </div>
          </ExportMenu>

          <button
            className="home-action-card med-primary-solid"
            onClick={() => navigate('/info_public#citation')}
          >
            <i className="bx bx-link-external"></i>
            <h3>Citing the EMRD</h3>
            <p>Information on how to properly cite the use of data from the EMRD.</p>
          </button>

          <button
            className="home-action-card med-primary-solid"
            onClick={() => navigate('/info_public')}
          >
            <i className="bx bx-info-circle"></i>
            <h3>About the EMRD</h3>
            <p>Learn more about the European Medicines Regulatory Database (EMRD) project, its methodology, and the collaborating institutions.</p>
          </button>


        </section>
        <section className="home-hero">
          <p class="home-undertitle">
            Developed and maintained by{" "}
            <a
              href="https://www.uu.nl/en/organisation/about-us"
              target="_blank"
              rel="noopener noreferrer"
            >
              Utrecht University <i class="bx bx-link-external"></i>
            </a>
            {" "} and the {" "}
            <a
              href="https://english.cbg-meb.nl/sections/about-meb"
              target="_blank"
              rel="noopener noreferrer"
            >
              Dutch Medicines Evaluation Board <i class="bx bx-link-external"></i>
            </a>.
          </p>
        </section>
      </div>

      {/* Analytics and Overview sections remain */}
      <section>
        <AnalyticsOverview data={data} />
      </section>

      {/* The old Tour Section is now removed */}

      {/* <section>
        <QuickOverview
          data={data}
          config={{
            detailsRoute: config.routes.main.details
          }}
        />
      </section> */}

      <section className="med-content-container">
        <MedicineGuessGame />
      </section>

      {/* The existing "Tools" info section remains
      <section className="med-content-container">
        <h1>Tools</h1>
        <hr className="med-top-separator" />

        <p>
          This dashboard provides tools for looking up, filtering, and
          visualizing data points regarding pharmaceutical policy and
          regulation. All functionality is split up into two pages: <i>Data</i>{' '}
          and <i>Visualize</i>.
        </p>

        <article>
          <h2>Data</h2>
          <p>
            The data page is optimized to find data points quickly. Searching
            can be done based on keywords and letter combinations, but filters
            can also be applied to ensure that only data points that meet
            specific (parameter) requirements appear. The data points can be
            sorted by a parameter of your choice. Finally, a selection can be
            made manually from the remaining data points, which can be used for
            visualization or exported to a local file if desired.
          </p>
        </article>

        <article>
          <h2>Visualize</h2>
          <p>
            The data points selected on the data page can be displayed
            graphically on the visualization page. An unlimited amount of
            visualizations can be added, each with its own title. There are four
            types of charts to choose from: a bar chart, a line chart, a pie
            chart, and a histogram. For each of these chart types, it can be
            determined which variables are shown on the axes and whether labels
            or a legend should be shown. For these variables, you can choose
            which values/categories are shown or skipped in the diagram. For bar
            charts, you can also choose to display the chart horizontally and to
            stack the bars (“Stacked”) so that fewer bars need to be shown. If
            the latter is chosen, you can also choose to make all bars the same
    length (“Fully stacked”) so that the graph is expressed in relative
            quantities. For line charts and histograms, it is possible to zoom
            in on a subset of the diagram. For any diagram, all quantities are
            clickable, which allows for the export or removal of specific data
            points. The resulting visualization can be exported to a local image
            file if desired.
          </p>
        </article>
      </section> */}
    </div>
  )
}

export default HomePage